import React, { useState, useEffect } from 'react';
import { ContractDocument, Horse } from './types';
import { fetchContractDocuments, getContractDocumentUrl, deleteContractDocument, analyzeContractDocument } from './api';
import { loadHorsesData } from './horsesData';
import ContractMatchReview from './ContractMatchReview';
import './ContractDocumentList.css';

interface Props {
  contractId?: string;
  horseId?: string;
  refreshTrigger?: number;
}

const ContractDocumentList: React.FC<Props> = ({ contractId, horseId, refreshTrigger }) => {
  const [documents, setDocuments] = useState<ContractDocument[]>([]);
  const [horses, setHorses] = useState<Horse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [analyzingId, setAnalyzingId] = useState<number | null>(null);
  const [matchReview, setMatchReview] = useState<{
    documentId: number;
    extractedData: any;
    matches: any[];
  } | null>(null);

  const loadDocuments = () => {
    setIsLoading(true);
    fetchContractDocuments(contractId, horseId)
      .then(docs => {
        setDocuments(docs);
        setIsLoading(false);
      })
      .catch(err => {
        console.error('Failed to load documents:', err);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    loadDocuments();
    loadHorsesData()
      .then(({ horses }) => setHorses(horses))
      .catch(() => {});
  }, [contractId, horseId, refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (doc: ContractDocument) => {
    if (!window.confirm(`"${doc.original_filename}" wirklich löschen?`)) return;
    try {
      await deleteContractDocument(doc.id);
      setDocuments(documents.filter(d => d.id !== doc.id));
    } catch (error: any) {
      alert(`Fehler: ${error.message}`);
    }
  };

  const handleView = (doc: ContractDocument) => {
    window.open(getContractDocumentUrl(doc.id), '_blank');
  };

  const handleAnalyze = async (doc: ContractDocument) => {
    setAnalyzingId(doc.id);
    try {
      // If already analyzed, reuse stored data (backend skips OCR+LLM)
      const result = await analyzeContractDocument(doc.id);
      setMatchReview({
        documentId: doc.id,
        extractedData: result.extracted_data,
        matches: result.matches,
      });
    } catch (error: any) {
      alert(`Analyse fehlgeschlagen: ${error.message}`);
    } finally {
      setAnalyzingId(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getMatchLabel = (status: string): string => {
    switch (status) {
      case 'confirmed': return 'Zugeordnet';
      case 'suggested': return 'Vorschlag';
      case 'manual': return 'Manuell';
      default: return 'Offen';
    }
  };

  if (isLoading) {
    return <div className="doc-list-loading">Lade Dokumente...</div>;
  }

  if (documents.length === 0) {
    return <div className="doc-list-empty">Keine Vertragsdokumente vorhanden.</div>;
  }

  return (
    <div className="contract-document-list">
      <table className="doc-table">
        <thead>
          <tr>
            <th>Dateiname</th>
            <th>Typ</th>
            {!horseId && <th>Pferd</th>}
            <th>Status</th>
            <th>Größe</th>
            <th>Datum</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {documents.map(doc => (
            <tr key={doc.id}>
              <td className="doc-filename">{doc.original_filename}</td>
              <td>
                <span className={`doc-type doc-type-${doc.document_type}`}>
                  {doc.document_type === 'generated' ? 'Erstellt' : 'Hochgeladen'}
                </span>
              </td>
              {!horseId && <td>{doc.horse_name || '-'}</td>}
              <td>
                <span className={`match-status match-${doc.match_status}`}>
                  {getMatchLabel(doc.match_status)}
                </span>
              </td>
              <td>{formatFileSize(doc.file_size_bytes)}</td>
              <td>{formatDate(doc.upload_date)}</td>
              <td className="doc-actions">
                <button onClick={() => handleView(doc)} className="btn-doc-view" title="Anzeigen">
                  PDF
                </button>
                {doc.document_type === 'uploaded' && (
                  <button
                    onClick={() => handleAnalyze(doc)}
                    className={`btn-doc-analyze ${doc.llm_analysis ? 'has-analysis' : ''}`}
                    title={doc.llm_analysis ? 'KI-Ergebnis anzeigen' : 'KI-Analyse starten'}
                    disabled={analyzingId === doc.id}
                  >
                    {analyzingId === doc.id ? '...' : 'KI'}
                  </button>
                )}
                <button onClick={() => handleDelete(doc)} className="btn-doc-delete" title="Löschen">
                  X
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {matchReview && (
        <ContractMatchReview
          documentId={matchReview.documentId}
          extractedData={matchReview.extractedData}
          matches={matchReview.matches}
          horses={horses}
          onClose={() => setMatchReview(null)}
          onMatched={() => loadDocuments()}
        />
      )}
    </div>
  );
};

export default ContractDocumentList;
